import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Ads, StickyAds } from "../../ads/Ads";
import { GlobalShare } from "../../SocialButtons";
import { debounce } from "lodash";
import Loader from "../../Loader";
import { NextArrow, PrevArrow } from "../../ArrowCarousel";
import TaleOfTheTapeSelectItem from "./TaleOfTheTapeSelectItem";
import TaleOfTheTapeInfoSection from "./TaleOfTheTapeInfoSection";
import styles from "../../../styles/races/national/TaleOfTheTape.module.scss";

const TaleOfTheTape = ({ presidential, vPresidential }) => {
  const router = useRouter();
  const [link, setLink] = useState("");
  const [query, setQuery] = useState({
    race: "presidential",
    selectedCandidates: undefined,
  });
  const [raceType, setRaceType] = useState(
    query.race ? query.race : "presidential"
  );
  const [candidateList, setCandidateList] = useState(
    raceType === "presidential" ? [...presidential] : [...vPresidential]
  );
  const [selectedCandidates, setSelectedCandidates] = useState([{}, {}, {}]);
  const [isCandidateAvailable, setIsCandidateAvailable] = useState(false);
  const [isCandidateLoading, setIsCandidateLoading] = useState(true);
  const [headerScrollLeft, setHeaderScrollLeft] = useState(0);

  useEffect(() => {
    if (router.isReady) {
      const queryParams = {
        race: ["presidential", "vice-presidential"].includes(router.query.race)
          ? router.query.race
          : "presidential",
        selectedCandidates: router.query.selectedCandidates?.split(","),
      };
      setQuery(queryParams);
      setRaceType(queryParams.race);

      const candidates =
        queryParams.race === "presidential"
          ? [...presidential]
          : [...vPresidential];
      setCandidateList(candidates);

      const selectedArray = [{}, {}, {}];
      if (
        queryParams.selectedCandidates &&
        queryParams.selectedCandidates[0] !== ""
      ) {
        queryParams.selectedCandidates.forEach((selectedCandidateId, index) => {
          const candidate = candidates.find(
            (candidate) => candidate.id === parseInt(selectedCandidateId)
          );
          if (candidate) {
            selectedArray[index] = candidate;
          }
        });
        setIsCandidateAvailable(true);
      }

      setSelectedCandidates(selectedArray);
      setIsCandidateLoading(false);
    }
  }, [router, presidential, vPresidential]);

  useEffect(() => {
    if (router.isReady) {
      const selectedCandidateIDs = [];
      selectedCandidates.forEach((selectedCandidate) => {
        if (selectedCandidate.id) {
          selectedCandidateIDs.push(selectedCandidate.id);
        }
      });

      const selectedCandidateParams = "";
      if (selectedCandidateIDs.length) {
        selectedCandidateIDs.forEach((selectedCandidateID) => {
          if (selectedCandidateParams) {
            selectedCandidateParams = `${selectedCandidateParams}%2C`;
          }
          selectedCandidateParams = `${selectedCandidateParams}${selectedCandidateID}`;
        });
      }
      const pageUrl = `${process.env.SITE_URL}elections/2022/races/president-vice-president/tale-of-the-tape?selectedCandidates=${selectedCandidateParams}&race=${raceType}`;
      window.history.replaceState("", "", pageUrl);
      setLink(pageUrl);
    }
  }, [router.isReady, selectedCandidates, raceType]);

  const handleScroll = debounce((scrollEvent) => {
    const { target } = scrollEvent;
    const syncedElements = document.querySelectorAll("[data-tott-scroller]");
    syncedElements.forEach((element) => {
      if (element.scrollLeft === target.scrollLeft) {
        return;
      }
      if (
        target.getAttribute("data-tott-scroller-type") ===
        element.getAttribute("data-tott-scroller-type")
      ) {
        return;
      }
      element.scrollTo({
        left: target.scrollLeft,
        behavior: "instant",
      });
    });
    const header = document.querySelector("[data-tott-scroller-type='header']");
    setHeaderScrollLeft(header.scrollLeft);
    // Removing this 0ms debounce breaks autoScroll
  }, 0);

  const autoScroll = (direction) => {
    let scrollTo;
    const header = document.querySelector("[data-tott-scroller-type='header']");
    const headerScrollLeft = Math.ceil(header.scrollLeft);
    if (headerScrollLeft < 270) {
      scrollTo = direction === "left" ? 0 : 270;
    } else if (headerScrollLeft < 320) {
      scrollTo = direction === "left" ? 0 : maxScrollPosition();
    } else {
      scrollTo = direction === "left" ? 270 : maxScrollPosition();
    }

    if (headerScrollLeft === scrollTo) {
      scrollTo = headerScrollLeft + (direction === "left" ? -270 : 270);
    }

    header.scrollTo({
      left: scrollTo,
      behavior: "smooth",
    });
  };

  const maxScrollPosition = () => {
    const header = document.querySelector("[data-tott-scroller-type='header']");
    if (header) {
      return header.scrollWidth - header.clientWidth;
    } else {
      return 99999;
    }
  };

  const selectRace = (race) => {
    if (race !== raceType) {
      setRaceType(race);
      setSelectedCandidates([{}, {}, {}]);
      setIsCandidateAvailable(false);

      if (race === "presidential") {
        setCandidateList([...presidential]);
      } else {
        setCandidateList([...vPresidential]);
      }
    }
  };

  const selectCandidate = (index, candidate) => {
    const selectedArray = [...selectedCandidates];
    selectedArray[index] = {};
    selectedArray[index] = { ...candidate };
    setSelectedCandidates(selectedArray);
    setIsCandidateAvailable(true);
  };

  const getSectionItems = (type) => {
    const items = [];
    switch (type) {
      case "bio":
        items = selectedCandidates.map(
          (selectedCandidate) => selectedCandidate.bio
        );
        return items;
      case "highlights-controversies":
        items = selectedCandidates.map(
          (selectedCandidate) => selectedCandidate.highlightsControversies
        );
        return items;
      case "promises":
        items = selectedCandidates.map(
          (selectedCandidate) => selectedCandidate.promises
        );
        return items;
      case "stances":
        items = selectedCandidates.map(
          (selectedCandidate) => selectedCandidate.stances
        );
        return items;
      default:
        return items;
    }
  };

  return (
    <div className={styles.taleOfTheTapeContainer}>
      <StickyAds
        id="ad-tape-sticky-desktop"
        deviceType="desktop"
        pageType="elec_tott_page"
      />
      <StickyAds
        id="ad-tape-sticky-mobile"
        deviceType="mobile"
        pageType="elec_tott_page"
      />
      <div className={styles.adsContainer}>
        <Ads
          deviceType="mobile"
          id="ad-tape-billboard-mobile"
          adType="billboard"
          pageType="elec_tott_page"
        />
        <Ads
          deviceType="desktop"
          id="ad-tape-billboard-desktop"
          adType="billboard"
          pageType="elec_tott_page"
        />
      </div>
      <div className={styles.headerWrap}>
        <div className={styles.header}>
          <h1 className={styles.title}>Tale of the Tape</h1>
          <p className={styles.description}>
            Get to know your candidates. Compare their highlights,
            controversies, their promises, and their stances on key issues. You
            can compare up to{" "}
            <span className={styles.bold}>3 candidates at a time</span>.
          </p>
          <div className={styles.pills}>
            <div
              className={raceType === "presidential" ? styles.active : ""}
              onClick={() => selectRace("presidential")}
            >
              President
            </div>
            <div
              className={raceType === "vice-presidential" ? styles.active : ""}
              onClick={() => selectRace("vice-presidential")}
            >
              Vice President
            </div>
          </div>
        </div>
        <div className={styles.adMiddle1}>
          <Ads
            deviceType="mobile"
            id="ad-tape-middle1-mobile"
            adType="middle1"
            pageType="elec_tott_page"
          />
          <Ads
            deviceType="desktop"
            id="ad-tape-middle1-desktop"
            adType="middle1"
            pageType="elec_tott_page"
          />
          <GlobalShare
            title="Share this page"
            url={link}
            fbQuote="Tale of the Tape - 2022 Philippine elections #PHVote #WeDecide"
            tWQuoute="Tale of the Tape - 2022 Philippine elections #PHVote #WeDecide"
            toolTipId={"toolTip_tott_mobile"}
            isMounted={true}
          />
        </div>
      </div>
      {isCandidateLoading ? (
        <Loader />
      ) : (
        <div className={styles.body}>
          <div
            className={`${styles.section} ${styles.candidateSelector}`}
            style={{ marginBottom: "20px" }}
          >
            <div className={styles.sideNav}>
              <h3 className={styles.sectionTitle}>
                Select{" "}
                {raceType === "presidential"
                  ? "Presidential"
                  : "Vice Presidential"}{" "}
                Candidates
              </h3>
            </div>
            <div
              className={styles.content}
              onScrollCapture={handleScroll}
              data-tott-scroller
              data-tott-scroller-type="header"
            >
              {selectedCandidates.map((_, index) => {
                return (
                  <div key={index} className={styles.column}>
                    <div className={styles.candidateSelectWrapper}>
                      <TaleOfTheTapeSelectItem
                        index={index}
                        candidates={candidateList}
                        selectedCandidates={selectedCandidates}
                        selectCandidate={selectCandidate}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {headerScrollLeft > 1 && (
              <div
                onClickCapture={() => autoScroll("left")}
                className={`${styles.scrollArrow} ${styles.prev}`}
              >
                <PrevArrow />
              </div>
            )}
            {headerScrollLeft < maxScrollPosition() && (
              <div
                onClickCapture={() => autoScroll("right")}
                className={`${styles.scrollArrow} ${styles.next}`}
              >
                <NextArrow />
              </div>
            )}
          </div>
          {isCandidateAvailable && (
            <TaleOfTheTapeInfoSection
              raceType={raceType}
              type={"bio"}
              headline={"Who is He/She?"}
              items={getSectionItems("bio")}
              selectedCandidates={selectedCandidates}
              scrollHandler={handleScroll}
            />
          )}
          {isCandidateAvailable && (
            <TaleOfTheTapeInfoSection
              raceType={raceType}
              type={"highlights-controversies"}
              headline={"Highlights & Controversies"}
              items={getSectionItems("highlights-controversies")}
              selectedCandidates={selectedCandidates}
              scrollHandler={handleScroll}
            />
          )}
          {isCandidateAvailable && (
            <TaleOfTheTapeInfoSection
              raceType={raceType}
              type={"promises"}
              headline={"Promises"}
              items={getSectionItems("promises")}
              selectedCandidates={selectedCandidates}
              scrollHandler={handleScroll}
            />
          )}
          {isCandidateAvailable && (
            <TaleOfTheTapeInfoSection
              raceType={raceType}
              type={"stances"}
              headline={"Stances on Major Issues"}
              items={getSectionItems("stances")}
              selectedCandidates={selectedCandidates}
              scrollHandler={handleScroll}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TaleOfTheTape;
